import constants from '@atlas/tool/box/constants';
import isNotFalsy from '@atlas/tool/box/isNotFalsy';

/**
 * Creates a URL to a transformed file from the FileStack CDN.
 *
 * For image sources, it is resized; for other formats, a preview is rendered.
 *
 * Docs: https://www.filestack.com/docs/api/processing/#image-transformations
 *
 * Examples:
 * - https://cdn.fs.atlas.so/auto_image/resize=width:1300/:filestack-handle
 * - https://cdn.fs.atlas.so/output=format:jpg/resize=width:100/:filestack-handle
 * - https://cdn.fs.atlas.so/:filestack-api-key/output=format:png/resize=height:100/https://files.atlas.so/path/to/image
 *
 * @param {TFilestackTransformationInput} params - The input containing a handle and/or URL.
 * @param {TFilestackTransformationOptions} [options] - Options for transforming the file URL.
 * @returns {string} - The generated Filestack CDN URL.
 */
export default function createFilestackImageURI(
    { handle, url }: TFilestackTransformationInput,
    options?: TFilestackTransformationOptions,
): string {
    const identifierPart = handle ?? url;
    if (!identifierPart) return '';

    if (!options) return url ?? `${constants.FILE_PICKER_CDN}/${handle}`;

    const accessKeyPart = handle ? '' : constants.FILE_PICKER_API_KEY; // required for a public URL
    const outputPart =
        options.output === 'auto' ? 'auto_image' : options.output ? `output=format:${options.output}` : '';
    const resizePart = options.resize
        ? 'resize=' +
          Object.entries(options.resize)
              .filter(([_, value]) => isNotFalsy(value))
              .map(([key, value]) => `${key}:${value}`)
              .join(',')
        : '';

    const pathParts = [accessKeyPart, outputPart, resizePart, identifierPart].filter(isNotFalsy);

    let filestackUrl;
    try {
        filestackUrl = new URL(pathParts.join('/'), constants.FILE_PICKER_CDN);
    } catch (error) {
        console.error('Error parsing Filestack URL\n', error);
        return '';
    }
    return filestackUrl.href;
}

// #region Types

export type TFilestackTransformationInput = { handle: string; url?: string } | { handle?: string | null; url: string };

type TFilestackTransformationOptions = {
    // "auto" picks up the format automatically (usually webp) and only works for image inputs
    // empty value keeps the original format
    output?: 'auto' | 'jpg' | 'png' | 'webp';
    resize?: TResizeOneDimension | TResizeBothDimensions;
};
type TResizeOneDimension = ({ width: number; height?: never } | { width?: never; height: number }) & {
    fit: 'clip' | 'max'; // If a single dimension is provided, only clip (default) or max fit can be applied.
};
type TResizeBothDimensions = { width: number; height: number } & {
    fit: 'clip' | 'max' | 'crop' | 'scale';
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('createFilestackImageURI', async () => {
        it('generates link out of given handle and options', () => {
            const fsOrigin = constants.FILE_PICKER_CDN + '/';
            const input = { handle: 'HANDLE' };

            expect(createFilestackImageURI(input)).toBe(fsOrigin + 'HANDLE');

            expect(createFilestackImageURI(input, { output: 'auto' })).toBe(fsOrigin + 'auto_image/HANDLE');
            expect(createFilestackImageURI(input, { output: 'jpg' })).toBe(fsOrigin + 'output=format:jpg/HANDLE');
            expect(createFilestackImageURI(input, { output: 'png' })).toBe(fsOrigin + 'output=format:png/HANDLE');

            expect(createFilestackImageURI(input, { resize: { width: 100, fit: 'max' } })).toBe(
                fsOrigin + 'resize=width:100,fit:max/HANDLE',
            );
            expect(createFilestackImageURI(input, { resize: { width: 100, height: undefined, fit: 'max' } })).toBe(
                fsOrigin + 'resize=width:100,fit:max/HANDLE',
            );
            expect(createFilestackImageURI(input, { resize: { height: 200, fit: 'clip' } })).toBe(
                fsOrigin + 'resize=height:200,fit:clip/HANDLE',
            );
            expect(createFilestackImageURI(input, { resize: { height: 200, width: undefined, fit: 'clip' } })).toBe(
                fsOrigin + 'resize=height:200,fit:clip/HANDLE',
            );
            expect(createFilestackImageURI(input, { resize: { width: 300, height: 400, fit: 'crop' } })).toBe(
                fsOrigin + 'resize=width:300,height:400,fit:crop/HANDLE',
            );
            expect(createFilestackImageURI(input, { output: 'auto', resize: { width: 500, fit: 'clip' } })).toBe(
                fsOrigin + 'auto_image/resize=width:500,fit:clip/HANDLE',
            );
        });

        it('generates link out of given public url and options', () => {
            const authenticatedOrigin = `${constants.FILE_PICKER_CDN}/${constants.FILE_PICKER_API_KEY}/`;
            const input = { url: 'URL' };

            expect(createFilestackImageURI(input)).toBe('URL');

            expect(createFilestackImageURI(input, { output: 'auto' })).toBe(authenticatedOrigin + 'auto_image/URL');
            expect(createFilestackImageURI(input, { output: 'jpg' })).toBe(
                authenticatedOrigin + 'output=format:jpg/URL',
            );
            expect(createFilestackImageURI(input, { output: 'png' })).toBe(
                authenticatedOrigin + 'output=format:png/URL',
            );

            expect(createFilestackImageURI(input, { resize: { width: 100, fit: 'max' } })).toBe(
                authenticatedOrigin + 'resize=width:100,fit:max/URL',
            );
            expect(createFilestackImageURI(input, { resize: { width: 100, height: undefined, fit: 'max' } })).toBe(
                authenticatedOrigin + 'resize=width:100,fit:max/URL',
            );
            expect(createFilestackImageURI(input, { resize: { height: 200, fit: 'clip' } })).toBe(
                authenticatedOrigin + 'resize=height:200,fit:clip/URL',
            );
            expect(createFilestackImageURI(input, { resize: { height: 200, width: undefined, fit: 'clip' } })).toBe(
                authenticatedOrigin + 'resize=height:200,fit:clip/URL',
            );
            expect(createFilestackImageURI(input, { resize: { width: 300, height: 400, fit: 'crop' } })).toBe(
                authenticatedOrigin + 'resize=width:300,height:400,fit:crop/URL',
            );
            expect(createFilestackImageURI(input, { output: 'auto', resize: { width: 500, fit: 'max' } })).toBe(
                authenticatedOrigin + 'auto_image/resize=width:500,fit:max/URL',
            );
        });

        it('handle is preferred for transformation when url is present', () => {
            const fsOrigin = constants.FILE_PICKER_CDN + '/';
            const input = { handle: 'HANDLE', url: 'URL' };

            expect(createFilestackImageURI(input)).toBe('URL');

            expect(createFilestackImageURI(input, { output: 'auto' })).toBe(fsOrigin + 'auto_image/HANDLE');
            expect(createFilestackImageURI(input, { output: 'jpg' })).toBe(fsOrigin + 'output=format:jpg/HANDLE');

            expect(createFilestackImageURI(input, { output: 'auto', resize: { width: 500, fit: 'max' } })).toBe(
                fsOrigin + 'auto_image/resize=width:500,fit:max/HANDLE',
            );
        });
    });
}

// #endregion
